import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import type { ToastShowOptions } from '../context/ToastContext'
import { showApiErrorToast } from '../lib/apiErrorToast'
import { useScrollPullToRefresh } from './useScrollPullToRefresh'

const TIMELINE_REFRESH_TIMEOUT_MS = 45_000

export type UseTimelineRefreshControllerOptions = {
  scrollRef: RefObject<HTMLElement | null>
  attachSignal: number
  enabled: boolean
  hydrated: boolean
  remoteLoading: boolean
  refreshTimeline: () => Promise<unknown>
  shouldIgnorePullStart: (target: EventTarget | null) => boolean
  showToast: (options: ToastShowOptions) => string
}

export function useTimelineRefreshController({
  scrollRef,
  attachSignal,
  enabled,
  hydrated,
  remoteLoading,
  refreshTimeline,
  shouldIgnorePullStart,
  showToast,
}: UseTimelineRefreshControllerOptions) {
  const [refreshing, setRefreshing] = useState(false)
  const resetPullGestureRef = useRef<() => void>(() => {})
  const refreshingRef = useRef(false)
  const refreshRunRef = useRef(0)
  const refreshOnceRef = useRef<() => Promise<void>>(async () => {})

  useEffect(() => {
    refreshingRef.current = refreshing
  }, [refreshing])

  const refreshOnce = useCallback(async () => {
    if (refreshingRef.current) return
    const runId = refreshRunRef.current + 1
    refreshRunRef.current = runId
    refreshingRef.current = true
    setRefreshing(true)
    resetPullGestureRef.current()
    let refreshOk = false
    try {
      await refreshTimeline()
      if (refreshRunRef.current !== runId) return
      refreshOk = true
    } catch (error) {
      showApiErrorToast(showToast, error, '刷新时间线失败', 'warning')
    } finally {
      if (refreshRunRef.current === runId) {
        refreshingRef.current = false
        setRefreshing(false)
        resetPullGestureRef.current()
        if (refreshOk) {
          showToast({ message: '时间线已更新', variant: 'success' })
        }
      }
    }
  }, [refreshTimeline, showToast])

  useEffect(() => {
    refreshOnceRef.current = refreshOnce
  }, [refreshOnce])

  const pull = useScrollPullToRefresh({
    scrollRef,
    attachSignal,
    enabled: enabled && hydrated,
    refreshing,
    shouldIgnorePullStart,
    onCommit: () => {
      void refreshOnceRef.current()
    },
  })

  useLayoutEffect(() => {
    resetPullGestureRef.current = pull.resetPullGesture
  }, [pull.resetPullGesture])

  const handleRefreshTimeout = useCallback(() => {
    resetPullGestureRef.current()
    refreshRunRef.current += 1
    refreshingRef.current = false
    setRefreshing(false)
    showToast({
      message: '刷新时间线超时，请稍后再试',
      variant: 'warning',
    })
  }, [showToast])

  useEffect(() => {
    if (!refreshing) return
    const id = window.setTimeout(() => {
      handleRefreshTimeout()
    }, TIMELINE_REFRESH_TIMEOUT_MS)
    return () => window.clearTimeout(id)
  }, [handleRefreshTimeout, refreshing])

  return {
    refreshing,
    pullPx: pull.pullPx,
    commitFlying: pull.commitFlying,
    onCommitFlyComplete: pull.onCommitFlyComplete,
    releaseThreshold: pull.releaseThreshold,
    indicatorRefreshing: refreshing || (!hydrated && remoteLoading),
  }
}
